//@flow

import React, { useState } from "react";
import { Field, useField } from "react-final-form";
import type { FieldRenderProps } from "react-final-form";
import { changeServiceFormInteraction } from "Commons/Utilities/GoogleTagManager";
import { Input } from "modules/ChangeServiceForm/components/shared/CommonComponents";
import { isDate } from "modules/ChangeServiceForm/misc/validations";

import Styles from "./DateInput.module.scss";

type Props = {
  fieldName: string,
  stepName: string,
  trackingEventLabel?: string,
  validate?: Function,
};

const DateInput = ({ fieldName, stepName, trackingEventLabel, validate }: Props) => {
  const currentDate = useField(fieldName).input.value;
  let initialDate = "";
  // Pre-fill the input if a valid date is already given
  // NOTE: isDate() is a validation function and should be false (means no errors)
  if (currentDate && !isDate(currentDate)) {
    const parts = currentDate.split("-");
    initialDate = `${parts[2]}.${parts[1]}.${parts[0]}`;
  }
  const [tempValue, setTempValue] = useState(initialDate);

  const formatDate = (value) => {
    // value format = TT.MM.JJJJ
    const parts = value.split(".");
    const dateFormatted = `${parts[2]}-${parts[1]}-${parts[0]}`;
    return dateFormatted;
    // return format = JJJJ-MM-TT
  };

  const onDateChange = (event, onChange: Function) => {
    let value = event.target.value;

    if (
      // if the input key press is not delete or backspace
      event.nativeEvent.inputType !== "deleteContentBackward" &&
      event.nativeEvent.inputType !== "deleteContentForward"
    ) {
      switch (value.length) {
        case 1:
          if (!/^[0-3]$/.test(value)) value = value.slice(0, -1);
          break;
        case 2:
          if (!/^[0-3][0-9]$/.test(value)) value = value.slice(0, -1);
          if (value.length === 2) {
            if (parseInt(value) > 31) value = value.slice(0, -1);
            else value = value + ".";
          }
          break;
        case 4:
          if (!/^[0-3][0-9]\.[0-1]$/.test(value)) value = value.slice(0, -1);
          break;
        case 5:
          if (!/^[0-3][0-9]\.[0-1][0-9]$/.test(value)) value = value.slice(0, -1);
          if (value.length === 5) {
            if (parseInt(value.split(".")[1]) > 12) value = value.slice(0, -1);
            else value = value + ".";
          }
          break;
        case 7:
          if (!/^[0-3][0-9]\.[0-1][0-9]\.[1-2]$/.test(value)) value = value.slice(0, -1);
          break;
        case 8:
          if (parseInt(value[value.length - 2]) === 2) {
            if (!/^[0-3][0-9]\.[0-1][0-9]\.[1-2][0]$/.test(value)) value = value.slice(0, -1);
          } else {
            if (!/^[0-3][0-9]\.[0-1][0-9]\.[1-2][9]$/.test(value)) value = value.slice(0, -1);
          }
          break;
        default:
          if (!/^[0-3][0-9]\.[0-1][0-9]\.[0-9]{0,4}$/.test(value)) value = value.slice(0, -1);
          break;
      }
    } else {
      switch (value.length) {
        case 2:
        case 5:
          // remove the "." when necessary
          value = value.slice(0, -1);
          break;
        default:
          // when the date is not complete or valid, clear the property
          if (value.length < 10) onChange(undefined);
          break;
      }
    }

    setTempValue(value);
    if (value.length === 10) onChange(formatDate(value));
    else if (value.length >= 1) onChange("???");
    else onChange(undefined);
  };

  return (
    <>
      <Field name={fieldName} validate={validate}>
        {({ input, meta }: FieldRenderProps) => {
          return (
            <Input
              className={Styles.dateInput}
              type="tel"
              maxLength="10"
              name={input.name}
              placeholder="TT.MM.JJJJ"
              errorMessage={meta.touched && meta.error}
              error={meta.error && (meta.dirty || meta.submitFailed) && meta.touched}
              value={tempValue}
              onBlur={input.onBlur}
              onChange={(event) => onDateChange(event, input.onChange)}
              onClick={() => !!trackingEventLabel && changeServiceFormInteraction(stepName, trackingEventLabel)}
            />
          );
        }}
      </Field>
    </>
  );
};

export default DateInput;
